import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

class MerchantService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  // Set auth token for requests
  setAuthToken(token) {
    console.log('MerchantService: Setting auth token:', token ? 'Token provided' : 'No token');
    if (token) {
      this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      console.log('MerchantService: Token set successfully');
    } else {
      delete this.api.defaults.headers.common['Authorization'];
      console.log('MerchantService: Token removed');
    }
  }

  // Product methods
  async getProducts() {
    try {
      console.log('MerchantService: Getting products with token:', this.api.defaults.headers.common['Authorization'] ? 'Token present' : 'No token');
      const response = await this.api.get('/api/merchant/products');
      console.log('MerchantService: Products response:', response.data);
      return response.data;
    } catch (error) {
      console.error('MerchantService: Error fetching products:', error.response?.data || error.message);
      console.error('MerchantService: Full error response:', error.response);
      throw error;
    }
  }

  async addProduct(productData) {
    try {
      console.log('MerchantService: Adding product with data:', productData);
      console.log('MerchantService: Using token:', this.api.defaults.headers.common['Authorization'] ? 'Token present' : 'No token');
      const response = await this.api.post('/api/merchant/products', productData);
      console.log('MerchantService: Add product response:', response.data);
      return response.data;
    } catch (error) {
      console.error('MerchantService: Error adding product:', error.response?.data || error.message);
      console.error('MerchantService: Full error response:', error.response);
      throw error;
    }
  }

  async updateProduct(productId, productData) {
    try {
      const response = await this.api.put(`/api/merchant/products/${productId}`, productData);
      return response.data;
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  }

  async deleteProduct(productId) {
    try {
      const response = await this.api.delete(`/api/merchant/products/${productId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  }

  // Device methods
  async getDevices() {
    try {
      console.log('MerchantService: Getting devices with token:', this.api.defaults.headers.common['Authorization'] ? 'Token present' : 'No token');
      const response = await this.api.get('/api/merchant/devices');
      console.log('MerchantService: Devices response:', response.data);
      return response.data;
    } catch (error) {
      console.error('MerchantService: Error fetching devices:', error.response?.data || error.message);
      console.error('MerchantService: Full error response:', error.response);
      throw error;
    }
  }

  async addDevice(deviceData) {
    try {
      console.log('MerchantService: Adding device with data:', deviceData);
      console.log('MerchantService: Using token:', this.api.defaults.headers.common['Authorization'] ? 'Token present' : 'No token');
      const response = await this.api.post('/api/merchant/devices', deviceData);
      console.log('MerchantService: Add device response:', response.data);
      return response.data;
    } catch (error) {
      console.error('MerchantService: Error adding device:', error.response?.data || error.message);
      console.error('MerchantService: Full error response:', error.response);
      console.error('MerchantService: Request config:', {
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers,
        data: error.config?.data
      });
      throw error;
    }
  }

  async updateDevice(deviceId, deviceData) {
    try {
      const response = await this.api.put(`/api/merchant/devices/${deviceId}`, deviceData);
      return response.data;
    } catch (error) {
      console.error('Error updating device:', error);
      throw error;
    }
  }

  async deleteDevice(deviceId) {
    try {
      const response = await this.api.delete(`/api/merchant/devices/${deviceId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting device:', error);
      throw error;
    }
  }

  // Profile methods
  async getProfile() {
    try {
      const response = await this.api.get('/api/merchant/profile');
      return response.data;
    } catch (error) {
      console.error('Error fetching profile:', error);
      throw error;
    }
  }

  async updateProfile(profileData) {
    try {
      const response = await this.api.put('/api/merchant/profile', profileData);
      return response.data;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }
}

// Named export for backward compatibility with Merchants.js
export async function fetchMerchants(token) {
  try {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';
    const response = await fetch(`${API_URL}/api/admin/merchants`, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch merchants: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Ensure we always return an array
    if (Array.isArray(data)) {
      return data;
    } else if (data && Array.isArray(data.data)) {
      return data.data;
    } else if (data && Array.isArray(data.merchants)) {
      return data.merchants;
    } else {
      console.warn('Unexpected API response format:', data);
      return [];
    }
  } catch (error) {
    console.error('Error fetching merchants:', error);
    // Return empty array instead of throwing to prevent component crash
    return [];
  }
}

// Fetch merchant analytics
export const fetchMerchantAnalytics = async (token) => {
  try {
    if (!token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`${API_BASE_URL}/api/merchant/analytics`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();
    if (response.ok && data.success) {
      return data.data;
    } else {
      throw new Error(data.error || 'Failed to fetch analytics');
    }
  } catch (error) {
    console.error('Error fetching merchant analytics:', error);
    throw error;
  }
};

// Fetch product sales analytics
export const fetchProductSalesAnalytics = async (token, params = {}) => {
  try {
    if (!token) {
      throw new Error('No authentication token available');
    }

    const queryParams = new URLSearchParams();
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    if (params.q) queryParams.append('q', params.q);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_order) queryParams.append('sort_order', params.sort_order);

    const url = `${API_BASE_URL}/api/merchants/analytics/products${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();
    if (response.ok && data.success) {
      return data.data;
    } else {
      throw new Error(data.error || 'Failed to fetch product sales analytics');
    }
  } catch (error) {
    console.error('Error fetching product sales analytics:', error);
    throw error;
  }
};

/**
 * Activate a merchant
 * @param {string} merchantId - The merchant ID
 * @param {string} token - Auth token
 * @param {Object} options - Activation options
 * @param {boolean} options.send_welcome_email - Whether to send welcome email
 * @param {string} [options.password] - Optional password for direct setup
 * @returns {Promise<Object>} Response with activation details
 */
export const activateMerchant = async (merchantId, token, options = {}) => {
  try {
    if (!token) {
      throw new Error('No authentication token available');
    }

    const body = {
      send_welcome_email: options.send_welcome_email !== false, // Default to true
    };

    // Add password if provided
    if (options.password) {
      body.password = options.password;
    }

    const response = await fetch(`${API_BASE_URL}/api/admin/merchants/${merchantId}/activate`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    const data = await response.json();

    // Return response with status code for proper handling
    return {
      status: response.status,
      success: data.success,
      message: data.message,
      data: data.data,
      error: data.error,
      code: data.code,
      details: data.details
    };
  } catch (error) {
    console.error('Error activating merchant:', error);
    throw error;
  }
};

/**
 * Resend password reset link to merchant
 * @param {string} merchantId - The merchant ID
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Response with resend status
 */
export const resendPasswordResetLink = async (merchantId, token) => {
  try {
    if (!token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`${API_BASE_URL}/api/admin/merchants/${merchantId}/resend-reset-link`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json();

    return {
      status: response.status,
      success: data.success,
      message: data.message,
      data: data.data,
      error: data.error,
      code: data.code
    };
  } catch (error) {
    console.error('Error resending password reset link:', error);
    throw error;
  }
};

const merchantService = new MerchantService();

export default merchantService; 